import type { FindAllUsersResponse } from "@dutyon/apis";
import {
  Button,
  formatDate,
  Loader,
  NursePositionBadge,
  RoleBadge,
  Stack,
  Table,
  Text,
} from "@dutyon/ui-kit";
import { Link } from "react-router-dom";

type UserWithNurse = FindAllUsersResponse["list"][number];

interface UserTableProps {
  users: UserWithNurse[];
  isLoading?: boolean;
  onDetailClick: (id: string) => void;
}

export function UserTable({ users, isLoading, onDetailClick }: UserTableProps) {
  const getProvider = (user: UserWithNurse) => {
    if (user.authProvider === "APPLE") return "Apple";
    if (user.authProvider === "FIREBASE") return "Firebase 연동";
    return "이메일";
  };

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>이름</Table.Th>
          <Table.Th>이메일</Table.Th>
          <Table.Th>소속 병원</Table.Th>
          <Table.Th>소속 팀</Table.Th>
          <Table.Th>직책</Table.Th>
          <Table.Th>전화번호</Table.Th>
          <Table.Th>권한</Table.Th>
          <Table.Th>가입일</Table.Th>
          <Table.Th>유형</Table.Th>
          <Table.Th>수정일</Table.Th>
          <Table.Th>관리</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {isLoading ? (
          <Table.Tr>
            <Table.Td colSpan={11} align="center" style={{ height: 100 }}>
              <Stack align="center" gap="xs">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                  사용자 목록을 불러오는 중입니다...
                </Text>
              </Stack>
            </Table.Td>
          </Table.Tr>
        ) : users.length === 0 ? (
          <Table.Tr>
            <Table.Td colSpan={11} align="center" style={{ height: 100 }}>
              <Text c="dimmed">데이터가 없습니다.</Text>
            </Table.Td>
          </Table.Tr>
        ) : (
          users.map((user) => {
            const latestNurse = user.nurses?.[0];
            return (
              <Table.Tr key={user.id}>
                <Table.Td>{user.name}</Table.Td>
                <Table.Td>{user.email}</Table.Td>
                <Table.Td>{latestNurse?.company?.name || "없음"}</Table.Td>
                <Table.Td>
                  {latestNurse?.team?.name ? (
                    <Text
                      component={Link}
                      to={`/companies/${latestNurse.companyId}`}
                      c="blue"
                      style={{ cursor: "pointer", textDecoration: "underline" }}
                    >
                      {latestNurse.team.name}
                    </Text>
                  ) : (
                    <Text c="dimmed">-</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <NursePositionBadge position={latestNurse?.position} />
                </Table.Td>
                <Table.Td>{user.phone || "-"}</Table.Td>
                <Table.Td>{latestNurse ? <RoleBadge role={latestNurse.role} /> : "-"}</Table.Td>
                <Table.Td>{formatDate(latestNurse?.joinDate)}</Table.Td>
                <Table.Td>{getProvider(user)}</Table.Td>
                <Table.Td>{formatDate(user.updatedAt)}</Table.Td>
                <Table.Td>
                  <Button size="xs" variant="light" onClick={() => onDetailClick(user.id)}>
                    상세
                  </Button>
                </Table.Td>
              </Table.Tr>
            );
          })
        )}
      </Table.Tbody>
    </Table>
  );
}
